
import React, { useRef, useEffect } from "react";
import { Check } from "lucide-react";
import { useWizard } from "./FormWizardContext";
import { useIsMobile } from "@/hooks/use-mobile";

interface StepIndicatorProps {
  stepTitles: string[];
  disabledSteps?: number[];
}

export function StepIndicator({ stepTitles, disabledSteps = [] }: StepIndicatorProps) {
  const { currentStep, totalSteps, goToStep } = useWizard();
  const isMobile = useIsMobile();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to current step when it changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeStep = container.querySelector('[aria-current="step"]');
      
      if (activeStep) {
        const containerWidth = container.offsetWidth;
        const stepLeft = (activeStep as HTMLElement).offsetLeft;
        const stepWidth = (activeStep as HTMLElement).offsetWidth;
        
        // Center the current step
        container.scrollLeft = stepLeft - containerWidth / 2 + stepWidth / 2;
      }
    }
  }, [currentStep]);

  return (
    <div className="mb-8 w-full overflow-x-auto px-6 pb-4">
      <div 
        ref={scrollContainerRef}
        className="flex items-center justify-between min-w-fit"
        style={{ paddingRight: '2rem', paddingLeft: '2rem' }}
      >
        <nav 
          className="flex items-center w-full max-w-4xl justify-between" 
          aria-label="Progress"
          style={{ minWidth: isMobile ? stepTitles.length * 120 + 'px' : 'auto' }}
        >
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
            const isComplete = step < currentStep;
            const isCurrent = step === currentStep;
            const isDisabled = disabledSteps.includes(step - 1);
            const stepWidth = 100 / totalSteps;

            return (
              <div 
                key={step} 
                className="relative flex flex-1 flex-col items-center px-4"
                style={{ minWidth: isMobile ? '120px' : `${stepWidth}%` }}
              >
                {/* Circle Button */}
                <button
                  type="button"
                  className={`relative flex h-12 w-12 items-center justify-center rounded-full text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    isComplete
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : isCurrent
                      ? "bg-blue-100 text-blue-800 dark:bg-blue-900/70 dark:text-blue-200"
                      : isDisabled 
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                  }`}
                  onClick={() => !isDisabled && goToStep(step)}
                  disabled={isDisabled}
                  aria-current={isCurrent ? "step" : undefined}
                >
                  {isComplete ? (
                    <Check className="h-6 w-6" />
                  ) : (
                    <span>{step}</span>
                  )}
                </button>

                {/* Connector Line */}
                {step < totalSteps && (
                  <div className={`hidden md:block absolute top-6 w-full`}>
                    <div
                      className={`h-0.5 ${
                        step < currentStep
                          ? "bg-blue-600"
                          : "bg-gray-200 dark:bg-gray-700"
                      }`}
                      style={{ 
                        width: 'calc(100% - 4rem)', 
                        left: '50%',
                        position: 'absolute',
                        transform: 'translateX(2rem)'
                      }}
                    ></div>
                  </div>
                )}

                {/* Step Title - Ensure height is sufficient for two lines */}
                <div
                  className={`mt-3 text-center text-xs md:text-sm min-h-[40px] flex items-center ${
                    isCurrent
                      ? "font-medium text-blue-800 dark:text-blue-300"
                      : isDisabled
                      ? "text-gray-400 dark:text-gray-600"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {stepTitles[step - 1]}
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
